/**
 * Free Gift Interceptor (Optimized Response-Intercept Method)
 * Eliminates waterfall network requests by piggybacking on original requests,
 * cutting processing delay in half and ensuring flawless render synchronicity.
 */

(function initFreeGiftInterceptor() {
    if (window._freeGiftInterceptorInitialized) return;
    window._freeGiftInterceptorInitialized = true;

    const originalFetch = window.fetch;

    function getCartItemProperty(item, propName) {
        if (!item.properties) return null;
        if (Array.isArray(item.properties)) {
            const prop = item.properties.find(
                (p) => p[0] === propName || p.name === propName
            );
            return prop ? prop[1] || prop.value : null;
        }
        return item.properties[propName];
    }

    function getSectionsFromArgs(url, options) {
        let sections = null;
        try {
            const parsedUrl = new URL(url, window.location.origin);
            sections = parsedUrl.searchParams.get('sections');
        } catch (e) { }

        if (!sections && options && options.body) {
            try {
                if (typeof options.body === 'string') {
                    const bodyJson = JSON.parse(options.body);
                    if (bodyJson.sections) {
                        sections = Array.isArray(bodyJson.sections)
                            ? bodyJson.sections.join(',')
                            : bodyJson.sections;
                    }
                } else if (options.body instanceof FormData) {
                    sections = options.body.get('sections');
                }
            } catch (e) { }
        }
        return sections;
    }

    if (!originalFetch) return;

    window.fetch = async function (...args) {
        let urlStr = '';
        let options = {};

        if (typeof args[0] === 'string') {
            urlStr = args[0];
            options = args[1] || {};
        } else if (args[0] && args[0].url) {
            urlStr = args[0].url;
            options = args[0];
        }

        const isCartMutation =
            urlStr.includes('/cart/add') ||
            urlStr.includes('/cart/change') ||
            urlStr.includes('/cart/update') ||
            urlStr.includes('/cart/clear');

        if (!isCartMutation) {
            return originalFetch.apply(this, args);
        }

        const requestedSections = getSectionsFromArgs(urlStr, options);

        const response = await originalFetch.apply(this, args);

        try {
            const clone = response.clone();
            const data = await clone.json();

            if (data.status >= 400 || data.errors) {
                return response;
            }
            let fullCart = null;
            if (data.items && (urlStr.includes('/cart/change') || urlStr.includes('/cart/update') || urlStr.includes('/cart/clear'))) {
                fullCart = data;
            } else {
                const cartRes = await originalFetch(window.Shopify.routes.root + 'cart.js');
                fullCart = await cartRes.json();
            }

            const requiredGifts = {};
            let hasSubscriptions = false;

            if (window.freeGiftsMatrix && fullCart.items) {
                fullCart.items.forEach((item) => {
                    const isSubscription = !!item.selling_plan_allocation;
                    if (isSubscription) hasSubscriptions = true;

                    const productType = item.product_type;
                    const isFreeGift = !!getCartItemProperty(item, '_is_free_gift');

                    if (
                        !isFreeGift &&
                        isSubscription &&
                        productType &&
                        window.freeGiftsMatrix[productType]
                    ) {
                        const giftVariantId = window.freeGiftsMatrix[productType];
                        requiredGifts[giftVariantId] = 1;
                    }
                });
            }

            const rejectedGifts = new Set(
                JSON.parse(sessionStorage.getItem('rejectedDoseGifts') || '[]')
            );
            if (!hasSubscriptions) {
                sessionStorage.removeItem('rejectedDoseGifts');
                rejectedGifts.clear();
            }

            const giftsToAdd = [];
            const giftsToRemove = {};

            fullCart.items.forEach((item) => {
                const isFreeGift = !!getCartItemProperty(item, '_is_free_gift');
                if (isFreeGift) {
                    const gid = String(item.variant_id);

                    if (!requiredGifts[gid]) {
                        giftsToRemove[item.key] = 0;
                    } else {
                        if (item.quantity !== 1) {
                            giftsToRemove[item.key] = 1;
                        }
                        delete requiredGifts[gid];
                    }
                }
            });

            for (const gid of Object.keys(requiredGifts)) {
                // Enforce user preference explicitly
                if (!rejectedGifts.has(Number(gid))) {
                    giftsToAdd.push({
                        id: Number(gid),
                        quantity: 1,
                        properties: {
                            _is_free_gift: 'true',
                        },
                    });
                }
            }

            let modificationsMade = false;
            let finalSectionsData = null;

            const sectionQuery = requestedSections ? `?sections=${requestedSections}` : '';

            if (giftsToAdd.length > 0) {
                const sq = Object.keys(giftsToRemove).length > 0 ? '' : sectionQuery;

                const addRes = await originalFetch(`${window.Shopify.routes.root}cart/add.js${sq}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: giftsToAdd }),
                });
                const addData = await addRes.json();

                if (addData.sections) finalSectionsData = addData.sections;
                modificationsMade = true;
            }

            if (Object.keys(giftsToRemove).length > 0) {
                const updateRes = await originalFetch(`${window.Shopify.routes.root}cart/update.js${sectionQuery}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ updates: giftsToRemove }),
                });
                const updateData = await updateRes.json();

                if (updateData.sections) finalSectionsData = updateData.sections;
                modificationsMade = true;
            }

            if (modificationsMade) {
                if (finalSectionsData) {
                    data.sections = finalSectionsData;
                }

                return new Response(JSON.stringify(data), {
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                });
            }

            return response;
        } catch (e) {
            console.error('Free Gift Interceptor Error:', e);
            return response;
        }
    };
})();